import { IGameState } from '../../../State/Game/IGameState';
import { IUIState, IUIStatePrio } from '../../../State/UI/IUIState';
import PIXIRenderer from '../../Render/PIXIRenderer';
import BaseUISystem from '../BaseUISystem';
import Interactable from '../../../Component/Interactable';
import {
    GetAnimationClip,
    PlayAnimation,
    StartAnimation,
} from '../../Animation/AnimationHelper';
import { RenderSystemEvent } from '../../Render/RenderSystem';
import { StartAudio } from '../../Audio/AudioHelper';
import { AudioTrackType } from '../../Audio/AudioClip';
import {
    AUDIO_UI_POPUP_CLOSE_DEFAULT,
    AUDIO_UI_POPUP_OPEN_DEFAULT,
} from '../../Audio';
import BaseUIHandler from '../BaseUIHandler';

export enum MenuState {
    OPEN = 'open',
    CLOSED = 'closed',
}

export default class BaseMenu extends BaseUIHandler {
    public clipShow: string;
    public clipHide: string;

    public audioClipShow: string;
    public audioClipHide: string;

    public popupOverlayEnabled: boolean;

    public currentState: MenuState;

    /**
     * Elements to be enabled/disabled during animations.
     */
    private interactables: Interactable[];

    constructor(name: string, system: BaseUISystem) {
        super(name, system);
        this.clipShow = 'MenuShow';
        this.clipHide = 'MenuHide';
        this.audioClipShow = AUDIO_UI_POPUP_OPEN_DEFAULT;
        this.audioClipHide = AUDIO_UI_POPUP_CLOSE_DEFAULT;
        this.popupOverlayEnabled = true;
        this.currentState = MenuState.CLOSED;
        this.interactables = [];
    }

    public create() {
        if (this.active) return;
        this.createElement();
        this.interactables = this.element.getComponentsInChildren(Interactable);
        this.onCreate();

        this.element.container.resize(
            PIXIRenderer.renderer.width,
            PIXIRenderer.renderer.height
        );

        this.active = true;
    }

    // tslint:disable-next-line: no-empty
    public handleStateChanged(state: IGameState): void {}

    public *show(state: IUIState): IterableIterator<number> {
        if (!this.active) return;

        if (this.popupOverlayEnabled) {
            // Bring overlay and popup to front.

            this.system.scene.emit(
                RenderSystemEvent.BRING_TO_FRONT,
                this.system.overlayPopup
            );
            this.system.scene.emit(
                RenderSystemEvent.BRING_TO_FRONT,
                this.element
            );
            this.system.scene.emit(
                RenderSystemEvent.BRING_TO_FRONT,
                this.system.UIOverlay
            );
        }

        this.enableInteractables(false);
        this.onShow(state.params);

        if (this.popupOverlayEnabled) {
            StartAnimation(this.system.overlayPopup, 'OverlayFadeIn');
        }

        StartAudio(this.system.scene, this.audioClipShow, AudioTrackType.SFX);

        yield* this.playShowAnimation();

        this.enableInteractables(true);
        this.onShown(state.params);
    }
    public *showPrio(state: IUIStatePrio): IterableIterator<number> {
        if (!this.active) return;

        if (this.popupOverlayEnabled) {
            // Bring overlay and popup to front.

            this.system.scene.emit(
                RenderSystemEvent.BRING_TO_FRONT,
                this.system.overlayPopupPrio
            );
            this.system.scene.emit(
                RenderSystemEvent.BRING_TO_FRONT,
                this.element
            );
            this.system.scene.emit(
                RenderSystemEvent.BRING_TO_FRONT,
                this.system.UIOverlay
            );
        }

        this.enableInteractables(false);
        this.onShow(state.paramsPrio);

        if (this.popupOverlayEnabled) {
            StartAnimation(this.system.overlayPopupPrio, 'OverlayFadeIn');
        }

        StartAudio(this.system.scene, this.audioClipShow, AudioTrackType.SFX);

        yield* this.playShowAnimation();

        this.enableInteractables(true);
        this.onShown(state.paramsPrio);
    }

    public *hide(state: IUIState): IterableIterator<number> {
        this.enableInteractables(false);
        this.onHide(state.params);

        if (this.popupOverlayEnabled) {
            StartAnimation(this.system.overlayPopup, 'OverlayFadeOut');
        }

        StartAudio(this.system.scene, this.audioClipHide, AudioTrackType.SFX);

        yield* this.playHideAnimation();

        this.onHidden(state.params);

        if (!this.alwaysVisible) {
            this.destroy();
        }
    }

    public *hidePrio(state: IUIStatePrio): IterableIterator<number> {
        this.enableInteractables(false);
        this.onHide(state.paramsPrio);

        if (this.popupOverlayEnabled) {
            StartAnimation(this.system.overlayPopupPrio, 'OverlayFadeOut');
        }

        StartAudio(this.system.scene, this.audioClipHide, AudioTrackType.SFX);

        yield* this.playHideAnimation();

        this.onHidden(state.paramsPrio);

        if (!this.alwaysVisible) {
            this.destroy();
        }
    }

    protected enableInteractables(value: boolean): void {
        this.interactables.forEach(interactable => {
            interactable.enabled = value;
        });
    }

    protected playShowAnimation() {
        return PlayAnimation(GetAnimationClip(this.clipShow, this.element));
    }

    protected playHideAnimation() {
        return PlayAnimation(GetAnimationClip(this.clipHide, this.element));
    }
}
